import { useNotify } from '@/app/providers/notify';
import {
  getErrorMessageTyped,
  getLogger,
} from '@/shared/services';

export function fullLog (error: unknown, message: string): void {
  getLogger().log('error', error);
  getLogger('console').log('error', [message, '\n', error]);
}

async function notifyError (error: unknown, fallbackMessage?: string): Promise<void> {
  const notifier = useNotify();

  if (!notifier) {
    return;
  }

  const message = getErrorMessageTyped(error, fallbackMessage);

  await notifier.create({
    message,
    type: 'negative',
  });
}

type TFunction = ((...args: Array<never>) => unknown);

/**
 * Декорирует функцию try-catch блоком, позволяет просунуть колбэк на ошибку
 * @param fn целевая функция
 * @param onCatch колбэк на отлов ошибки
 * @returns декорированную функцию
 */
export function tryCatchDecorate<TFn extends TFunction> (fn: TFn, onCatch: (error: unknown) => void) {
  return (async (...args) => {
    try {
      return await fn(...args);
    } catch (error) {
      onCatch(error);
    }
  }) as TFn;
}

/**
 * Задекорирует функцию на логирование ошибки при помощи {@link notifyError}
 * @param fn целевая функция
 * @param message fallback-сообщение для тоаста (если не получилось вывести сообщение из ошибки)
 * @param rethrow выбрасывать ли исключение после логирования дальше
 */
export function notifyDecorate<TFn extends TFunction> (fn: TFn, message: string, rethrow = false): TFn {
  return rethrow
    ? tryCatchDecorate(fn, async (error) => {
        await notifyError(error, message);
        throw error;
      })
    : tryCatchDecorate(fn, async (error) => notifyError(error, message));
}

/**
 * Задекорирует функцию на логирование ошибки при помощи {@link fullLog}
 * @param fn целевая функция
 * @param message сообщение для консоли (традиционно здесь примерно такое → `🚀 ~ file: /news/index.vue ~ getNextPage ~ error`)
 * @param rethrow выбрасывать ли исключение после логирования дальше
 */
export function fullLogDecorate<TFn extends TFunction> (fn: TFn, message: string, rethrow = false): TFn {
  return rethrow
    ? tryCatchDecorate(fn, (error) => {
        fullLog(error, message);
        throw error;
      })
    : tryCatchDecorate(fn, (error) => fullLog(error, message));
}

/**
 * Задекорирует все функции в полях `actions` при помощи {@link fullLogDecorate} на логирование {@link fullLog}
 * @param actions объект с методами, которые нужно задекорировать
 * @param filename для удобства отладки - имя файла, где используется
 * @param rethrow выбрасывать ли исключение после логирования дальше
 */
export function decorateApiActions<TActions extends Record<string, TFunction>> (actions: TActions, filename: string, rethrow = false): TActions {
  const decorated = {} as TActions;
  for (const key in actions) {
    decorated[key] = fullLogDecorate(actions[key], `🚀 ~ file: ${ filename } ~ ${ key } ~ error`, rethrow);
  }

  return decorated;
}
